import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Card, Form, InputGroup, Badge, Spinner } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import slackService from '../services/slackService';
import './SlackChat.css';

// Polling disabled - manual refresh only

const SlackChat = ({ isOpen, onToggle, onUnreadCountChange }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const lastSeenMessageIdRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Fetch messages from Slack
  const fetchMessages = useCallback(async (isInitialLoad = false) => {
    try {
      const status = slackService.getConnectionStatus();
      // Only poll from real Slack, not mock mode
      if (!status.hasToken && !isInitialLoad) {
        return;
      }

      const recentMessages = await slackService.getRecentMessages();
      const fetchedMessages = recentMessages || [];

      // Update connection status after fetch (in case it changed due to errors)
      const newStatus = slackService.getConnectionStatus();
      setConnectionStatus(newStatus);

      setMessages((prevMessages) => {
        // Separate local messages (ones we sent that might not be in Slack yet)
        // Local messages have numeric IDs from Date.now(), Slack messages have string IDs like "1234567890.123456"
        const localOnlyMessages = prevMessages.filter(
          (m) => typeof m.id === 'number' || !String(m.id).includes('.')
        );

        // Create a set of Slack message IDs
        const slackIds = new Set(fetchedMessages.map((m) => String(m.id)));

        // Keep local messages that aren't in Slack yet
        const pendingLocalMessages = localOnlyMessages.filter((m) => !slackIds.has(String(m.id)));

        // Combine: Slack messages + pending local messages
        const combined = [...fetchedMessages];

        // Add pending local messages at the end if any
        if (pendingLocalMessages.length > 0) {
          combined.push(...pendingLocalMessages);
        }

        return combined;
      });
    } catch (error) {
      // Silently handle errors to avoid console spam
    }
  }, []);

  useEffect(() => {
    // Initialize Slack connection and load messages
    const initializeChat = async () => {
      setIsLoading(true);

      try {
        // Initialize Slack service
        const connected = await slackService.initialize();
        setIsConnected(connected);
        setConnectionStatus(slackService.getConnectionStatus());

        // Load recent messages
        await fetchMessages(true);

        // Add message listener for real-time updates (mock mode)
        const handleNewMessage = (message) => {
          setMessages((prev) => [...prev, message]);
        };

        slackService.addMessageListener(handleNewMessage);

        // Cleanup function
        return () => {
          slackService.removeMessageListener(handleNewMessage);
        };
      } catch (error) {
        console.error('Failed to initialize chat:', error);
        setIsConnected(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeChat();
  }, [fetchMessages]);

  // Polling disabled - use manual refresh button instead

  useEffect(() => {
    // Auto-scroll to bottom when new messages arrive
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const root = document.documentElement;
    if (isOpen) {
      root.classList.add('slack-chat-open');
      document.body.classList.add('slack-chat-open');
    } else {
      root.classList.remove('slack-chat-open');
      document.body.classList.remove('slack-chat-open');
    }

    return () => {
      root.classList.remove('slack-chat-open');
      document.body.classList.remove('slack-chat-open');
    };
  }, [isOpen]);

  useEffect(() => {
    // Track unread messages
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];

      if (isOpen) {
        // When chat is open, mark all as read
        lastSeenMessageIdRef.current = lastMessage.id;
        setUnreadCount(0);
      } else {
        // When chat is closed, count new messages from others
        if (lastSeenMessageIdRef.current !== lastMessage.id) {
          const isFromOthers =
            lastMessage.user !== user?.username &&
            lastMessage.user !== user?.first_name &&
            lastMessage.type !== 'user';
          if (isFromOthers) {
            setUnreadCount((prev) => prev + 1);
            lastSeenMessageIdRef.current = lastMessage.id;
          }
        }
      }
    }
  }, [messages, isOpen, user]);

  // Use ref for callback to avoid effect loop when parent doesn't memoize
  const onUnreadCountChangeRef = useRef(onUnreadCountChange);
  onUnreadCountChangeRef.current = onUnreadCountChange;

  useEffect(() => {
    if (onUnreadCountChangeRef.current) {
      onUnreadCountChangeRef.current(unreadCount);
    }
  }, [unreadCount]);

  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const messageText = newMessage;
    const userName = user?.first_name || user?.username || 'You';

    // Add user message to chat immediately
    const userMessage = {
      id: Date.now(),
      user: userName,
      text: messageText,
      timestamp: new Date(),
      type: 'user',
    };

    setMessages((prev) => [...prev, userMessage]);
    setNewMessage('');

    try {
      // Send message through Slack service
      const result = await slackService.sendMessage(messageText, userName);

      if (!result.success) {
        // Show error message if sending failed
        const errorMessage = {
          id: Date.now() + 1,
          user: 'System',
          text: `Failed to send message: ${result.error}. Please try again.`,
          timestamp: new Date(),
          type: 'system',
        };
        setMessages((prev) => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        user: 'System',
        text: 'Failed to send message. Please check your connection and try again.',
        timestamp: new Date(),
        type: 'system',
      };
      setMessages((prev) => [...prev, errorMessage]);
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getMessageTypeClass = (type) => {
    switch (type) {
      case 'system':
        return 'message-system';
      case 'support':
        return 'message-support';
      case 'user':
        return 'message-user';
      default:
        return 'message-default';
    }
  };

  return (
    <>
      {/* Chat Panel */}
      <div
        className={`slack-chat-panel ${isOpen ? 'open' : ''}`}
        ref={chatContainerRef}
        role="dialog"
        aria-modal={isOpen}
        aria-hidden={!isOpen}
        aria-label="Support Chat"
      >
        <Card className="h-100">
          <Card.Header className="d-flex justify-content-between align-items-center bg-primary text-white">
            <div className="d-flex align-items-center">
              <i className="fab fa-slack me-2"></i>
              <span>Support Chat</span>
              {isConnected ? (
                <Badge bg="success" className="ms-2">
                  <i className="fas fa-circle me-1" style={{ fontSize: '8px' }}></i>
                  {connectionStatus?.hasToken ? 'Slack Connected' : 'Demo Mode'}
                </Badge>
              ) : (
                <Badge bg="warning" className="ms-2">
                  <i className="fas fa-circle me-1" style={{ fontSize: '8px' }}></i>
                  Connecting...
                </Badge>
              )}
            </div>
            <div className="d-flex align-items-center">
              {connectionStatus?.hasToken && (
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={() => fetchMessages()}
                  className="me-2"
                  title="Refresh messages"
                >
                  <i className="fas fa-sync-alt"></i>
                </Button>
              )}
              <Button
                variant="outline-light"
                size="sm"
                onClick={onToggle}
                className="chat-close-btn"
              >
                <i className="fas fa-times"></i>
              </Button>
            </div>
          </Card.Header>

          <Card.Body className="chat-messages p-0">
            {isLoading ? (
              <div className="d-flex justify-content-center align-items-center h-100">
                <Spinner animation="border" variant="primary" />
                <span className="ms-2">Connecting to support...</span>
              </div>
            ) : (
              <div className="messages-container" ref={messagesContainerRef}>
                {(messages || []).map((message) => (
                  <div key={message.id} className={`message ${getMessageTypeClass(message.type)}`}>
                    <div className="message-header">
                      <strong className="message-user">{message.user}</strong>
                      <small className="message-time text-muted">
                        {formatTime(message.timestamp)}
                      </small>
                    </div>
                    <div className="message-text">{message.text}</div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </Card.Body>

          <Card.Footer className="p-2">
            <Form onSubmit={handleSendMessage}>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="Type your message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  disabled={!isConnected}
                />
                <Button
                  type="submit"
                  variant="primary"
                  disabled={!newMessage.trim() || !isConnected}
                >
                  <i className="fas fa-paper-plane"></i>
                </Button>
              </InputGroup>
            </Form>
            <div className="chat-footer-info">
              <small className="text-muted">
                <i className="fas fa-info-circle me-1"></i>
                {connectionStatus?.hasToken
                  ? 'Connected to Slack workspace'
                  : 'Demo mode - Configure Slack for live chat'}
              </small>
            </div>
          </Card.Footer>
        </Card>
      </div>

      {/* Overlay for mobile */}
      {isOpen && (
        <div className="chat-overlay" onClick={onToggle} role="presentation" aria-hidden="true" />
      )}
    </>
  );
};

export default SlackChat;
